import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { namespace } from '@common/config/constants';
import {
  DBCampaignThunks,
  DBDripThunks,
  campaignsSelectOneObjectByQuery,
  dripsSelectOneObjectByQuery,
} from '@common-reducers/DBServiceThunks';
import { CampaignWizard, type WizardStepId } from './wizard';
import { SequenceDetail } from './sequence-detail';
import { resolveCampaignEditSurface } from './campaign-editability';

/**
 * The `/campaigns/:id/edit/:step` route used to render the drip wizard
 * UNCONDITIONALLY. But the wizard hydrates by DRIP id
 * (`dripsSelectOneObjectByQuery({_id})`), so navigating it with a STANDALONE
 * campaign id hydrated nothing and the route hung on "Loading…" forever — the
 * customer's "no way to edit my scheduled campaign" (FE#214).
 *
 * This dispatcher resolves the opaque `:id` to the right editor:
 *   - a DRIP        → the wizard (unchanged);
 *   - a standalone campaign that can still be edited → the campaign PAGE (the
 *     same place the 3-dot menu's Edit goes), whose Audience / When tabs and
 *     step Edit save through the server-authoritative calls, so the server (not
 *     the stale cached status) decides whether the edit is allowed;
 *   - terminal/unknown, or an id that resolves to neither once both fetches have
 *     settled → a graceful notice, never an infinite spinner.
 */
export const CampaignEditRoute: React.FC<{
  id: string;
  step: WizardStepId;
  onBack: () => void;
}> = ({ id, step, onBack }) => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();

  // The route param is opaque about whether it is a drip or a standalone
  // campaign, so fetch BOTH and let identity decide. `settled` flips once both
  // requests have resolved, so we can show "not found" instead of spinning
  // forever when the id matches nothing.
  const [settled, setSettled] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    setSettled(false);
    Promise.allSettled([
      dispatch(DBDripThunks.find({ _id: id })),
      dispatch(DBCampaignThunks.find({ _id: id })),
    ]).finally(() => {
      if (alive) setSettled(true);
    });
    return () => {
      alive = false;
    };
  }, [dispatch, id]);

  const drip = useAppSelector((s) => dripsSelectOneObjectByQuery(s.DBDripReducer, { _id: id }));
  const campaign = useAppSelector((s) => campaignsSelectOneObjectByQuery(s.DBCampaignReducer, { _id: id }));

  // A drip → the wizard it was built for (behavior unchanged for the common path).
  if (drip) return <CampaignWizard key={id} dripId={id} step={step} />;

  // Standalone campaign → the tier editor. The `key` remounts on id change (each
  // modal seeds its working copy once from the campaign doc).
  if (campaign) {
    const surface = resolveCampaignEditSurface({ isDrip: false, campaignStatus: campaign.campaignStatus });
    // Still editable → the campaign's PAGE, which is where a campaign is
    // edited: its message (through the step's Edit), and — until the first
    // message is sent — its audience and send time (the Audience / When tabs).
    if (surface === 'schedule-editor' || surface === 'campaign-editor') {
      return <SequenceDetail key={id} dripId={id} onBack={onBack} />;
    }
    return <EditNotice text={t('campaignsRedesign.editRoute.finished', "This campaign can't be edited — it has already finished sending.")} onBack={onBack} />;
  }

  // Neither resolved. Spin only until both fetches settle, then say so.
  if (!settled) return <EditNotice text={t('campaignsRedesign.wizard.loading', 'Loading…')} onBack={onBack} showBack={false} />;
  return <EditNotice text={t('campaignsRedesign.editRoute.notFound', 'This campaign could not be found.')} onBack={onBack} />;
};

const EditNotice: React.FC<{ text: string; onBack: () => void; showBack?: boolean }> = ({
  text,
  onBack,
  showBack = true,
}) => {
  const { t } = useTranslation(namespace);
  return (
  <div
    style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      color: 'var(--text-secondary, #64748b)',
    }}
  >
    <div>{text}</div>
    {showBack && (
      <button
        type="button"
        onClick={onBack}
        style={{
          padding: '8px 16px',
          borderRadius: 8,
          border: '1px solid var(--border, #e2e8f0)',
          background: 'var(--bg-surface, #fff)',
          cursor: 'pointer',
        }}
      >
        {t('campaignsRedesign.backToCampaigns', 'Back to campaigns')}
      </button>
    )}
  </div>
  );
};
